import type { ReactElement, ReactNode } from 'react';
import type { Path } from 'react-hook-form';
import { Controller, useFormContext } from 'react-hook-form';
import type { ActionMeta, GroupBase, Props, SingleValue } from 'react-select';

import Select from 'components/Select/Select';
import type { SelectOptionT } from 'declarations/types/select';

import ErrorMessage from '../ErrorMessage/ErrorMessage';

type FormSelectPropsT<
  FormValuesT,
  Option extends SelectOptionT = SelectOptionT,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>
> = Omit<Props<Option, IsMulti, Group>, 'options' | 'value' | 'onChange' | 'defaultValue'> & {
  name: Path<FormValuesT>;
  options: SelectOptionT[];
  defaultValue?: SelectOptionT['value'];
  placeholder?: string;
  isCompactMenuMode?: boolean;
  label?: string | ReactNode;
  isRequired?: boolean;
  onValueChange?: ((
    newValue: SingleValue<SelectOptionT>,
    actionMeta: ActionMeta<SelectOptionT>
  ) => void) &
    ((option: SingleValue<string | number>) => void);
};

const FormSelect = <FormValuesT,>({
  name,
  onValueChange,
  ...restFormSelectProps
}: FormSelectPropsT<FormValuesT>): ReactElement => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  return (
    <>
      <Controller
        name={name}
        control={control}
        render={({ field: { ref: _ref, ...restFieldProps } }): ReactElement => (
          <Select {...restFieldProps} {...restFormSelectProps} onValueChange={onValueChange} />
        )}
      />
      {errors[name] && <ErrorMessage error={errors[name]?.message as Path<FormValuesT>} />}
    </>
  );
};

export default FormSelect;
